import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Box,
  Button,
  CardBody,
  CardFooter,
  CardHeader,
  Divider,
  Heading,
  Icon,
  Stack,
  Text,
  Tooltip,
} from '@chakra-ui/react';
import { CheckCircleIcon, ExclamationCircleIcon } from '@heroicons/react/outline';
import dayjs from 'dayjs';
import { useRef, useState } from 'react';
import { useTranslations } from 'use-intl';

import { nationalityOptions } from '@blockpulse3/data/shared';
import {
  DraftRepresentative,
  IdentityType,
  RepresentativeRole,
  SelectData,
} from '@blockpulse3/graphql/hooks';
import { formatCountry } from '@blockpulse3/helpers';
import { useExpandAccordion } from '@blockpulse3/ui/ui-hooks';
import { useUser } from '@blockpulse3/web-client/auth';

import {
  RepresentantInformationsForm,
  isFormValid,
  representantTitle,
} from '../../Forms/RepresentantInformationsForm';
import { useSPVForm } from '../../provider';
import { draftRepresentativeDefaultValues } from '../../provider/schema';

type Props = {
  /* ** Back step callback ** */
  onCancel: () => void;
  /* ** Next step callback ** */
  onSubmit: (data: DraftRepresentative[]) => void;
};

/**
 * RepresentantInformationsStep.
 * SPV.
 * Step that renders the form for the representant of the company.
 * It is splitted on purpose, to keep the form agnostic.
 *
 * @param {Props}
 * @returns {JSX.Element}
 */
export function RepresentantInformationsStep({ onCancel, onSubmit }: Props): JSX.Element {
  /* ** Ref to externaly submit nested accordion forms (Legal,Natural RepresentantForm) ** */
  const formRef = useRef<HTMLFormElement>(null);

  const i18nGeneral = useTranslations('General');
  const i18nRoleValues = useTranslations('RoleValues');
  const i18nRepresentantInformationsStep = useTranslations('SPVRepresentantInformationsStep');

  const { user } = useUser();

  const { companyDraftContent } = useSPVForm();
  const { representatives } = companyDraftContent;

  /* ** Additional representants ** */
  const [records, setRecords] = useState<DraftRepresentative[]>(representatives);
  /* ** Controls expended accordions ** */
  const { indexes, toggle, collapse } = useExpandAccordion(
    /* ** Close all accordion by default ** */
    Object.keys(representatives).reduce((acc, cur) => ({ ...acc, [cur]: [] }), {}),
  );

  /* ** Update the state of the expended accordion by its id ** */
  const handleIndexChange = (id: number): void => {
    toggle(id.toString());
  };

  /* ** Returns representant record, go to next step ** */
  const handleStepSubmit = (): void => {
    /* ** Submit event is sent to the nested form, calling useForm()'s handleSubmit() ** */
    formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    /* ** Each new added representant is validated, to pass to the next step ** */
    const isValid = Object.values(records).every((rep) => isFormValid(rep));
    if (isValid) {
      onSubmit(records);
    }
  };

  /* ** Cancel, go back to previous step ** */
  const handleStepCancel = (): void => {
    onCancel();
  };

  /* ** Fill a representant entry in the record ** */
  const handleAddRepresentant = (id: number, data: DraftRepresentative): void => {
    setRecords((prevRecords) => {
      const newRecords = [...prevRecords];
      newRecords[id] = data;
      return newRecords;
    });
    collapse(id.toString());
  };

  // TODO: better management of representant creation form
  /* ** Add a new representant form, with default values ** */
  // const handleNewRepresentantForm = (): void => {
  //   /* ** An id is generated for better control (deletion, fill, react keys) ** */
  //   expand(records.length.toString());
  //   setRecords((prevRecords) => [...prevRecords, draftRepresentativeDefaultValues]);
  // };

  /* ** Delete representant using generated nanoid ** */
  const handleDeleteRepresentant = (id: number): void => {
    setRecords((prevRecords) => {
      const newRecords: DraftRepresentative[] = [];
      prevRecords.forEach((rec, index) => {
        if (index !== id) {
          newRecords.push(rec);
        }
      });
      return newRecords;
    });
  };

  /* ** Fill role record with user identity informations ** */
  const handleUserIdentityChange = (id: number, isSelected: boolean): void => {
    if (isSelected && user?.individualIdentity) {
      const { individualIdentity } = user;
      const date = dayjs(individualIdentity.birthdate).format('YYYY-MM-DD');
      /* ** Set state with identity values from the workspace when the switch is on ** */
      setRecords((prevRecords) => {
        const newRecords = [...prevRecords];
        newRecords[id] = {
          ...prevRecords[id],
          type: IdentityType.INDIVIDUAL,
          firstName: individualIdentity.firstName || '',
          lastName: individualIdentity.lastName || '',
          email: individualIdentity.email,
          birthdate: date,
          birthplace: individualIdentity.birthplace || '',
          address: {
            line: individualIdentity.address?.line || '',
            city: individualIdentity.address?.city || '',
            postalCode: individualIdentity.address?.postalCode || '',
            country: individualIdentity.address?.country || '',
            countryData: {
              label: formatCountry(individualIdentity.address?.country || ''),
              value: individualIdentity.address?.country || '',
            },
          },
          nationalityData: nationalityOptions.find(
            (option) => option.value === individualIdentity.nationality,
          ) as SelectData,
        };
        return newRecords;
      });
    } else {
      /* ** Set state with schema default values when the switch is off ** */
      setRecords((prevRecords) => {
        const newRecords = [...prevRecords];
        newRecords[id] = draftRepresentativeDefaultValues;
        return newRecords;
      });
    }
  };

  /* ** Fill role record with default legal values on legal switch toggle ** */
  const handleIdentityTypeChange = (id: number): void => {
    setRecords((prevRecords) => {
      const newRecords = [...prevRecords];
      newRecords[id] = draftRepresentativeDefaultValues;
      return newRecords;
    });
  };

  return (
    <>
      <CardHeader as={Stack} spacing="2">
        <Heading size="lg">{i18nRepresentantInformationsStep('Title')}</Heading>
        <Text color="gray.600" fontSize="lg">
          {i18nRepresentantInformationsStep('SubTitle')}
        </Text>
      </CardHeader>
      <Divider />
      <CardBody as={Stack} spacing="4">
        {records.map((record, id) => {
          const isMain = id === 0 || record.role === RepresentativeRole.PRESIDENT;
          const heading = isMain
            ? RepresentativeRole.PRESIDENT
            : RepresentativeRole.MANAGING_DIRECTOR;
          const roles: (keyof IntlMessages['RoleValues'])[] = isMain
            ? [RepresentativeRole.PRESIDENT]
            : [RepresentativeRole.MANAGING_DIRECTOR];

          return (
            <Accordion
              key={id}
              allowToggle
              index={indexes[id]}
              onChange={(): void => handleIndexChange(id)}
            >
              <AccordionItem mt="0">
                <AccordionButton>
                  {isFormValid(record) ? (
                    <Tooltip
                      hasArrow
                      label={i18nRepresentantInformationsStep('Tooltip.Success')}
                      placement="top"
                    >
                      <Icon as={CheckCircleIcon} boxSize="30" color="green.500" />
                    </Tooltip>
                  ) : (
                    <Tooltip
                      hasArrow
                      label={i18nRepresentantInformationsStep('Tooltip.Warning')}
                      placement="top"
                    >
                      <Icon as={ExclamationCircleIcon} boxSize="30" color="yellow.400" />
                    </Tooltip>
                  )}
                  <Box flex="1" p="2" textAlign="left">
                    <Heading fontSize="2xl">{i18nRoleValues(heading)}</Heading>
                    <Text>{representantTitle(record)}</Text>
                  </Box>
                  <AccordionIcon boxSize="8" />
                </AccordionButton>
                <AccordionPanel>
                  <RepresentantInformationsForm
                    defaultValues={record}
                    id={id}
                    ref={formRef}
                    roles={roles}
                    shouldCancel={isMain}
                    onCancel={(): void => handleDeleteRepresentant(id)}
                    onIdentityTypeChange={(): void => handleIdentityTypeChange(id)}
                    onSubmit={(data): void => handleAddRepresentant(id, data)}
                    onUserIdentityChange={(isSelected): void =>
                      handleUserIdentityChange(id, isSelected)
                    }
                  />
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
          );
        })}
        {/* <Button
            fontSize="3xl"
            justifyContent="start"
            leftIcon={<PlusIcon height="25" width="25" />}
            variant="container"
            onClick={handleNewRepresentantForm}
          >
            {i18nRepresentantInformationsStep('AddRepresentant')}
          </Button> */}
      </CardBody>
      <CardFooter as={Stack} direction={{ base: 'column', md: 'row' }} spacing="4">
        <Button variant="secondary" w="full" onClick={handleStepCancel}>
          {i18nGeneral('Back')}
        </Button>
        <Button w="full" onClick={handleStepSubmit}>
          {i18nGeneral('Next')}
        </Button>
      </CardFooter>
    </>
  );
}

export type RepresentantInformationsStepProps = Props;
